// features/intelligence/tabs/sources.js
// ═══════════════════════════════════════════════════════════
// SOURCES — everything Shift reads to produce intelligence.
//
// Reads /api/intelligence/sources which inspects the actual DB
// tables and reports each source's last fetched_at + computed
// health (healthy / stale / unseeded / static).
//
// Replaces the Phase 1 hardcoded sample list. Real source
// names, real timestamps, real health.
// ═══════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';

const STATUS_COLOR = {
  healthy:  '#7a9e82',  // sage
  stale:    '#c46050',  // terra
  unseeded: '#888888',  // muted — table empty, cron hasn't fired yet
  static:   '#5a9eb5',  // teal — hardcoded sources, always present
};

const STATUS_LABEL = {
  healthy:  'Healthy',
  stale:    'Stale',
  unseeded: 'No data yet',
  static:   'Hardcoded',
};

export default function SourcesTab() {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/intelligence/sources?domain=trading', { cache: 'no-store' });
        if (!res.ok) return;
        const payload = await res.json();
        if (cancelled) return;
        setSources(payload.sources || []);
      } catch (err) {
        console.warn('[sources] fetch failed:', err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const healthyCount = sources.filter(s => s.status === 'healthy' || s.status === 'static').length;
  const staleCount = sources.filter(s => s.status === 'stale').length;
  const unseededCount = sources.filter(s => s.status === 'unseeded').length;

  return (
    <div className="di-scroll">
      <div className="di-section" style={{ marginBottom: 16 }}>
        <div className="di-sec-header">
          <div className="di-sec-label">Connected Sources — Trading</div>
          <div className="di-sec-right">
            <div className="di-sec-time">
              {loading ? 'Loading…' : statusSummary({ healthyCount, staleCount, unseededCount, total: sources.length })}
            </div>
          </div>
        </div>
        <div style={{ padding: 0 }}>
          {sources.map((s, i) => (
            <SourceRow key={s.name} source={s} isLast={i === sources.length - 1} />
          ))}
          {!loading && sources.length === 0 && (
            <div style={{
              padding: '24px 18px',
              textAlign: 'center',
              color: '#888',
              fontFamily: 'DM Mono, monospace',
              fontSize: 11,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
            }}>
              No sources configured
            </div>
          )}
        </div>
      </div>

      {/* Contribute to the Community pool. These four entry types
          let practitioners nominate new sources, share research, and
          contribute domain knowledge. Submissions feed into the
          Community tab's contribution review queue (Phase 5).
          See HANDOFF.md for the full architecture. */}
      <div className="di-section">
        <div className="di-sec-header">
          <div className="di-sec-label">Contribute to the Community</div>
          <div className="di-sec-right">
            <div className="di-sec-time">Submissions feed the Community tab</div>
          </div>
        </div>
        <div className="di-sec-body" style={{ padding: '14px 18px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
            {[
              { glyph: '⌬', label: 'Nominate API',     sub: 'Suggest a data feed worth adding to the default set' },
              { glyph: '◇', label: 'Add RSS / URL',     sub: 'Newsletter, analyst feed, or research site to monitor' },
              { glyph: '↑', label: 'Upload Research',   sub: 'PDFs, transcripts, or papers worth sharing' },
              { glyph: '◈', label: 'Manual Context',    sub: 'Free-form observation or note for the community pool' },
            ].map(opt => (
              <button key={opt.label} type="button" style={{
                background: 'transparent', border: '1px dashed rgba(255,255,255,0.06)',
                borderRadius: 8, padding: '14px 16px', textAlign: 'left',
                cursor: 'pointer', color: '#888', fontFamily: 'DM Sans, sans-serif',
                display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <span style={{ fontSize: 18, color: '#5a9eb5' }}>{opt.glyph}</span>
                <span>
                  <div style={{ fontSize: 12, color: '#c8c4e0', marginBottom: 2 }}>{opt.label}</div>
                  <div style={{ fontSize: 10, color: '#555' }}>{opt.sub}</div>
                </span>
              </button>
            ))}
          </div>
          <div style={{
            marginTop: 14, fontFamily: 'DM Mono, monospace', fontSize: 9,
            color: '#555', letterSpacing: '0.08em', textTransform: 'uppercase',
          }}>
            Contribution flow ships with Community tab build
          </div>
        </div>
      </div>
    </div>
  );
}

function SourceRow({ source, isLast }) {
  const color = STATUS_COLOR[source.status] || '#888';
  return (
    <div style={{
      padding: '12px 18px',
      borderBottom: isLast ? 'none' : '1px solid #1e1e2a',
      display: 'grid',
      gridTemplateColumns: '12px 1fr auto auto',
      gap: 14,
      alignItems: 'center',
    }}>
      <div style={{
        width: 8, height: 8, borderRadius: '50%',
        background: color,
        boxShadow: source.status === 'healthy' ? `0 0 6px ${color}` : 'none',
      }} />
      <div>
        <div style={{ fontSize: 13, color: '#c8c4e0', marginBottom: 2 }}>{source.name}</div>
        <div style={{
          fontFamily: 'DM Mono, monospace', fontSize: 9,
          color: '#555', letterSpacing: '0.08em', textTransform: 'uppercase',
        }}>
          {source.kind} · {source.cadence}{source.feeds?.length ? ` · feeds ${source.feeds.join(', ')}` : ''}
        </div>
      </div>
      <div style={{
        fontFamily: 'DM Mono, monospace', fontSize: 9,
        color, textTransform: 'uppercase', letterSpacing: '0.1em',
      }}>
        {STATUS_LABEL[source.status] || source.status}
      </div>
      <div style={{
        fontFamily: 'DM Mono, monospace', fontSize: 10, color: '#888',
        minWidth: 100, textAlign: 'right',
      }}>
        {formatLastFetched(source)}
      </div>
    </div>
  );
}

function formatLastFetched(source) {
  if (source.status === 'static') return source.note ? '—' : '—';
  if (!source.lastFetched) return 'Never';
  const ageMin = source.ageMinutes;
  if (ageMin == null) return '—';
  if (ageMin < 1) return 'Just now';
  if (ageMin < 60) return `${ageMin}m ago`;
  const ageHr = Math.floor(ageMin / 60);
  if (ageHr < 24) return `${ageHr}h ago`;
  const ageDay = Math.floor(ageHr / 24);
  return `${ageDay}d ago`;
}

function statusSummary({ healthyCount, staleCount, unseededCount, total }) {
  if (staleCount > 0) return `${staleCount} stale · ${healthyCount}/${total} healthy`;
  if (unseededCount > 0) return `${unseededCount} pending first cron · ${healthyCount}/${total} healthy`;
  return `${healthyCount}/${total} healthy · all reporting`;
}
